import { INCLUSIVE_HST_FRACTION } from "../constants/tax";

export { INCLUSIVE_HST_FRACTION };

export function currentYearRange() {
  const year = new Date().getFullYear();
  return {
    year,
    fromIso: `${year}-01-01`,
    toIso: `${year}-12-31`,
    label: `January 01, ${year} to December 31, ${year}`,
  };
}

function toIsoDay(value) {
  if (!value) return "";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    return value.slice(0, 10);
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function isoInRange(iso, fromIso, toIso) {
  if (!iso) return false;
  if (fromIso && iso < fromIso) return false;
  if (toIso && iso > toIso) return false;
  return true;
}

export function netWithoutHst(netInclusive) {
  const net = Number(netInclusive) || 0;
  const hst = net * INCLUSIVE_HST_FRACTION;
  return Math.round((net - hst) * 100) / 100;
}

export function hstFromNet(netInclusive) {
  const net = Number(netInclusive) || 0;
  return Math.round(net * INCLUSIVE_HST_FRACTION * 100) / 100;
}

/**
 * Map commission-trust EFTs into payroll report rows (AgentCommissionTransfer).
 */
export function mapEftRows(efts, agents = []) {
  return (efts || []).map((eft) => {
    const agent = (agents || []).find(
      (a) =>
        String(a.employeeNo ?? "") === String(eft.agentId ?? "") ||
        String(a._id) === String(eft.agentId ?? ""),
    );
    const trade = eft.tradeId && typeof eft.tradeId === "object" ? eft.tradeId : null;
    const k = trade?.keyInfo || {};
    const address = [k.streetNumber, k.streetName, k.unit]
      .filter(Boolean)
      .join(" ")
      .trim();
    const issuedIso = toIsoDay(eft.chequeDate || eft.date);
    const closingIso = toIsoDay(k.closeDate || k.closingDate || k.finalizedDate);
    const net = Number(eft.amount) || 0;
    return {
      agentId: agent?.employeeNo || eft.agentId || "",
      agentName:
        eft.agentName ||
        [agent?.firstName, agent?.lastName].filter(Boolean).join(" ") ||
        "",
      tradeNumber: trade?.tradeNumber || "",
      address,
      issuedIso,
      closingIso,
      netCommission: net,
      hst: hstFromNet(net),
      totalPayment: netWithoutHst(net),
    };
  });
}

export function filterRowsForYear(rows, fromIso, toIso) {
  return (rows || []).filter((row) => {
    // Prefer issued (EFT) date; fall back to closing date so rows still show.
    const iso = row.issuedIso || row.closingIso;
    return isoInRange(iso, fromIso, toIso);
  });
}

/** One row per agent for Agent Summary. */
export function buildAgentSummaryRows(rows) {
  const byAgent = new Map();
  for (const row of rows || []) {
    const key = String(row.agentId || row.agentName || "").trim() || "unknown";
    if (!byAgent.has(key)) {
      byAgent.set(key, {
        agentId: row.agentId || "",
        agentName: row.agentName || "",
        totalPayment: 0,
        netCommission: 0,
        hst: 0,
      });
    }
    const agg = byAgent.get(key);
    agg.totalPayment += Number(row.totalPayment) || 0;
    agg.netCommission += Number(row.netCommission) || 0;
    agg.hst += Number(row.hst) || 0;
    if (!agg.agentName && row.agentName) agg.agentName = row.agentName;
  }
  return [...byAgent.values()]
    .map((r) => ({
      ...r,
      agentKey: agentFilterKey(r),
      totalPayment: Math.round(r.totalPayment * 100) / 100,
      netCommission: Math.round(r.netCommission * 100) / 100,
      hst: Math.round(r.hst * 100) / 100,
    }))
    .sort((a, b) =>
      String(a.agentName).localeCompare(String(b.agentName), undefined, {
        sensitivity: "base",
      }),
    );
}

/** Stable key for agent filter checkboxes. */
export function agentFilterKey(row) {
  return String(row?.agentId || row?.agentName || "").trim() || "unknown";
}

/** Trade-level rows for Total Details. */
export function buildTotalDetailsRows(rows) {
  return [...(rows || [])]
    .map((row) => ({
      agentId: row.agentId || "",
      agentName: row.agentName || "",
      agentKey: agentFilterKey(row),
      tradeNumber: row.tradeNumber || "",
      address: row.address || "",
      issuedDate: row.issuedIso || row.closingIso || "",
      totalPayment: Number(row.totalPayment) || 0,
    }))
    .sort((a, b) => String(a.issuedDate).localeCompare(String(b.issuedDate)));
}

export function formatMoney(n) {
  return new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
  }).format(Number(n) || 0);
}

export function formatIsoDisplay(iso) {
  if (!iso) return "";
  const [y, m, d] = String(iso).split("-");
  if (!y || !m || !d) return iso;
  return `${m}/${d}/${y}`;
}
